/**
 * Settings → Integrations (slice 3b spec §5.1; slice 6a §6.4; Paper 40, 40b,
 * 44, 74, 74b), from `GET /api/integrations`:
 * - **Yours:** a card per app with the person's own accounts, then their MCP
 *   servers. They change them here (Change access, Reconnect, Rename, Make
 *   primary, Disconnect), and each has a Shared / Just you switch.
 * - **Shared with you:** the others' shared ones, read-only, each with its
 *   connector.
 * - **Below:** Browse apps, Add by URL and Browse servers, for everyone.
 * - **Ask first** (slice 3 §3.1, Paper AP5): the owner's switch per write action.
 *
 * Coming back from Google, `?connected=` or `?error=` shows a banner, read
 * once; then the URL is cleaned. The `integrations` hub event refetches the
 * list, so a change elsewhere shows at once.
 */
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@crew/ui/components/alert-dialog";
import { Button, buttonVariants } from "@crew/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@crew/ui/components/dropdown-menu";
import { Input } from "@crew/ui/components/input";
import { Link } from "@crew/ui/components/link";
import { Popover, PopoverContent, PopoverTrigger } from "@crew/ui/components/popover";
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { ChevronRight, Ellipsis, Plus } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { ConnectWindow, type WindowApp, type WindowMode } from "./connect-window";
import {
  clearStarted,
  readStarted,
  useChangeAccount,
  useConnect,
  useDisconnectAccount,
  useIntegrations,
  useRetryDisconnect,
  type Account,
  type IntegrationApp,
  type Integrations,
  type Provider,
  type Started,
} from "./integrations-api";
import { integrationsKey } from "./api";
import {
  AppIcon,
  ErrorLine,
  PrimaryTag,
  ReturnBanner,
  SectionHead,
  SOLID_RED,
  StateLabel,
  UNAVAILABLE,
} from "./integrations-parts";
import { AskFirstOwner } from "./ask-first-view";
import { AskFirstLink } from "./ask-first-page";
import { ServerButtons, useServers } from "./mcp-section";
import { SharedWithYou, ShareSelect } from "./shared-section";
import { handOffSignIn, openSignInTab, type HandOff } from "./signin-tab";
import {
  byConnector,
  disconnectNotice,
  disconnectWords,
  liveWaits,
  MAX_ACCOUNTS_PER_APP,
  plural,
  returnBanner,
  rowState,
  type Banner,
  type RowState,
  waitEnds,
} from "./integrations-view";
import { startWaitClock, type Fired } from "./wait-clock";

/** The page's footnote: whose accounts a person's asks use (slice 6a §5.2). */
const FOOTNOTE =
  "When you ask, your crew uses your own accounts first, then the shared ones. Only the person who connected one can change it.";

type Query = { connected: string | null; error: string | null; reason: string | null };

/**
 * In a sign-in's own tab (`signin-tab.ts`), the outcome goes to the tab that
 * started it, and this one closes. `refused`: the browser kept it open, so
 * the page says it can be closed.
 */
function useHandOff() {
  const [handOff, setHandOff] = useState<HandOff>("none");
  const send = useCallback((query: Query) => {
    void handOffSignIn(query, {
      storage: window.sessionStorage,
      close: () => window.close(),
      isClosed: () => window.closed,
    })
      .then(setHandOff)
      .catch(() => setHandOff("none"));
  }, []);
  return [handOff, send] as const;
}

/** `?connected=` / `?error=` and the connect this tab started, read once; the URL is then cleaned. */
function useReturn(): [Query, Started | null, () => void, HandOff] {
  const [handOff, sendHandOff] = useHandOff();
  const [params, setParams] = useSearchParams();
  const { key } = useLocation();
  // Each callback is its navigation (`key`), not its query: the same code twice
  // is two callbacks, each with the connect it remembered (Devin on #247).
  const [ret, setRet] = useState(() => ({ key, query: queryOf(params), started: readStarted() }));
  // Every callback URL, the first and any later one this mounted tab is sent to:
  // read it, keep it for the banner, then clean the URL (Devin on #247).
  useEffect(() => {
    if (!params.has("connected") && !params.has("error")) return;
    const query = queryOf(params);
    const started = readStarted();
    clearStarted();
    sendHandOff(query);
    // StrictMode runs this twice for one navigation; the second keeps the first's.
    setRet((prev) => (prev.key === key ? prev : { key, query, started }));
    setParams(
      (p) => {
        p.delete("connected");
        p.delete("error");
        p.delete("reason");
        return p;
      },
      { replace: true },
    );
  }, [key, params, setParams, sendHandOff]);
  const dismiss = () =>
    setRet((prev) => ({
      key: prev.key,
      query: { connected: null, error: null, reason: null },
      started: null,
    }));
  return [ret.query, ret.query.connected || ret.query.error ? ret.started : null, dismiss, handOff];
}

/**
 * The server's time, a tick a second while a sign-in waits (`wait-clock.ts`),
 * from the list's `skew`; past a wait's end the list refetches (again while
 * it's still listed), so the server's tidy runs.
 */
function useServerNow(list: Integrations | undefined): number {
  const client = useQueryClient();
  const skew = list?.skew ?? 0;
  const [now, setNow] = useState(() => Date.now() + skew);
  const fired = useRef<Fired>(new Map());
  const ends = list ? waitEnds(list).join(",") : "";
  useEffect(() => {
    if (!ends) return;
    return startWaitClock({
      ends: ends.split(",").map(Number),
      skew,
      fired: fired.current,
      onTick: setNow,
      onPassed: () => void client.invalidateQueries({ queryKey: integrationsKey }),
    });
  }, [ends, skew, client]);
  return now;
}

const queryOf = (params: URLSearchParams): Query => ({
  connected: params.get("connected"),
  error: params.get("error"),
  reason: params.get("reason"),
});

/**
 * The list as it stands at the server's `now`: a sign-in whose link has
 * expired is gone at once, and the refetch has the server tidy it. A failed
 * load is no list, so its error shows, not the list it last had.
 */
function useLiveIntegrations() {
  const list = useIntegrations();
  const now = useServerNow(list.data);
  const data = list.data && !list.isError ? liveWaits(list.data, now) : undefined;
  return { list, now, data };
}

export function IntegrationsTab({ me }: { me: Me }) {
  const { list, now, data } = useLiveIntegrations();
  const [query, started, dismiss, handOff] = useReturn();
  const [notice, setNotice] = useState<Banner | null>(null);
  const [win, setWin] = useState<{ app: WindowApp; mode: WindowMode } | null>(null);
  const banner = notice ?? returnBanner(query, { apps: data?.apps, started });

  return (
    // Slice 3 §3.1 (Paper AP5): each app's What asks first, the owner's to change.
    <AskFirstOwner value={Boolean(data?.available) && me.user.role === "owner"}>
      <div className="flex flex-col gap-6" data-integrations>
        <PageBanner
          banner={banner}
          closeTab={handOff === "refused"}
          onClose={() => {
            setNotice(null);
            dismiss();
          }}
        />
        {list.isPending ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : !data ? (
          <p className="text-body text-destructive">{errorHint(list.error)}</p>
        ) : (
          <Sections
            data={data}
            me={me.user.id}
            timeZone={me.workspace.timezone}
            now={now}
            review={query.connected?.startsWith("mcp:") ? query.connected.slice(4) : null}
            onWindow={(app, mode) => setWin({ app, mode })}
            onNotice={(title) => setNotice({ tone: "moss", title })}
          />
        )}
        <p className="text-meta text-muted-foreground">{FOOTNOTE}</p>
        <ConnectWindow
          app={win?.app ?? null}
          mode={win?.mode ?? { kind: "connect" }}
          onClose={() => setWin(null)}
          onSaved={(title) => setNotice({ tone: "moss", title })}
        />
      </div>
    </AskFirstOwner>
  );
}

/**
 * Yours, Shared with you, then the ways to add one (slice 6a §6.4), from the
 * list as it stands at the server's `now`.
 */
function Sections({
  data,
  me,
  timeZone,
  now,
  review,
  onWindow,
  onNotice,
}: {
  data: Integrations;
  /** The signed-in person's id: their own rows are Yours. */
  me: string;
  timeZone: string;
  now: number;
  review: string | null;
  onWindow: (app: WindowApp, mode: WindowMode) => void;
  onNotice: (title: string) => void;
}) {
  const { mine, shared } = byConnector(data, me);
  const servers = useServers({
    servers: mine.mcp_servers,
    available: data.available,
    timeZone,
    now,
    review,
    onNotice,
  });
  return (
    <>
      <YoursSection
        apps={mine.apps}
        servers={servers.cards}
        available={data.available}
        timeZone={timeZone}
        now={now}
        onWindow={onWindow}
        onNotice={onNotice}
      />
      {data.available ? <SharedWithYou rows={shared} timeZone={timeZone} now={now} /> : null}
      {data.available ? <AddButtons onAdd={servers.add} /> : null}
      {servers.windows}
    </>
  );
}

/**
 * Below the lists (spec §6.4: unchanged, for everyone): Browse apps, the
 * catalog, and Add by URL and Browse servers. On a phone, Browse apps is the
 * page's one solid button (Paper 74b).
 */
function AddButtons({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col gap-2 md:flex-row" data-add-buttons>
      <Link
        href="/settings/integrations/apps"
        className={cn(buttonVariants(), "h-12 text-body md:hidden")}
      >
        Browse apps
      </Link>
      <BrowseAppsButton className="hidden md:inline-flex" />
      <ServerButtons className="grid grid-cols-2 md:flex" onAdd={onAdd} />
    </div>
  );
}

/** The banner at the top, and in a sign-in's tab the browser kept open, that it can be closed. */
function PageBanner({
  banner,
  closeTab,
  onClose,
}: {
  banner: Banner | null;
  closeTab: boolean;
  onClose: () => void;
}) {
  return (
    <>
      {banner ? <ReturnBanner banner={banner} onClose={onClose} /> : null}
      {closeTab ? (
        <p className="-mt-3 text-meta text-muted-foreground" data-close-tab>
          You can close this tab.
        </p>
      ) : null}
    </>
  );
}

function BrowseAppsButton({ className }: { className?: string }) {
  return (
    <Link
      href="/settings/integrations/apps"
      className={cn(buttonVariants({ variant: "outline" }), "bg-card", className)}
    >
      Browse apps
      <ChevronRight className="size-3" />
    </Link>
  );
}

/**
 * Yours (Paper 74, 74b): the person's own app cards, then their MCP servers,
 * each with its Who can use it dropdown. Everything here is theirs to change.
 */
function YoursSection({
  apps,
  servers,
  available,
  timeZone,
  now,
  onWindow,
  onNotice,
}: {
  apps: IntegrationApp[];
  /** Their MCP server cards (`useServers`). */
  servers: ReactNode[];
  available: boolean;
  timeZone: string;
  /** The server's time, for a sign-in's countdown. */
  now: number;
  onWindow: (app: WindowApp, mode: WindowMode) => void;
  onNotice: (title: string) => void;
}) {
  return (
    <section aria-label="Yours" className="flex flex-col gap-2.5 pt-2 md:gap-3">
      <SectionHead
        title="Yours"
        inline
        line={
          <>
            <span className="md:hidden">
              Your Chief of Staff uses these when you ask. Share one, and the whole crew can.
            </span>
            <span className="hidden md:inline">
              Connected by you. Share one, and anyone in the crew can ask with it, up to the access
              you gave it.
            </span>
          </>
        }
      />
      {apps.length === 0 && servers.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-apps-empty>
          {available
            ? "Nothing connected yet. Connect your mail, calendar, docs and files from Browse apps."
            : UNAVAILABLE}
        </p>
      ) : null}
      {apps.map((app) =>
        available ? (
          <AppCard
            key={app.app}
            app={app}
            timeZone={timeZone}
            now={now}
            onWindow={onWindow}
            onNotice={onNotice}
          />
        ) : (
          <UnavailableCard key={app.app} app={app} />
        ),
      )}
      {servers}
    </section>
  );
}

function UnavailableCard({ app }: { app: IntegrationApp }) {
  return (
    <div
      data-app={app.app}
      className="flex items-center gap-3.5 rounded-panel bg-card px-5 py-4 shadow-[0_0_0_1px_rgb(31_42_68/0.08)]"
    >
      <AppIcon app={app.app} icon={app.icon} mark={app.mark} muted />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-body font-semibold text-muted-foreground">{app.name}</span>
        <span className="text-meta text-muted-foreground">{UNAVAILABLE}</span>
      </div>
    </div>
  );
}

function AppCard({
  app,
  timeZone,
  now,
  onWindow,
  onNotice,
}: {
  app: IntegrationApp;
  timeZone: string;
  /** The server's time, for a sign-in's countdown. */
  now: number;
  onWindow: (app: WindowApp, mode: WindowMode) => void;
  onNotice: (title: string) => void;
}) {
  const live = app.accounts.filter((a) => a.status !== "connecting").length;
  const needsYou = app.accounts.some((a) => a.status === "expired" || a.status === "disconnecting");
  const count = plural(live, "account");
  return (
    <div
      data-app={app.app}
      className={cn(
        "flex flex-col rounded-panel bg-card",
        needsYou
          ? "shadow-[0_0_0_1px_rgb(162_59_47/0.4)]"
          : "shadow-[0_0_0_1px_rgb(31_42_68/0.08)]",
      )}
    >
      <div className="flex items-center gap-3 border-b border-border px-4 py-3.5 md:gap-3.5 md:px-5 md:py-4">
        <AppIcon app={app.app} icon={app.icon} mark={app.mark} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h3 className="text-body font-semibold text-foreground">{app.name}</h3>
          <span className="text-meta text-muted-foreground">
            <span className="hidden md:inline">{app.covers} · </span>
            {count}
          </span>
        </div>
        {app.can_add ? (
          <Button
            variant="outline"
            className="bg-card px-2.5 md:px-3"
            onClick={() => onWindow(app, { kind: "connect" })}
          >
            <Plus className="size-3" />
            <span className="md:hidden">Add</span>
            <span className="hidden md:inline">Add account</span>
          </Button>
        ) : null}
      </div>
      {app.accounts.map((a, i) => (
        <AccountRow
          key={a.id}
          app={app}
          account={a}
          timeZone={timeZone}
          now={now}
          last={i === app.accounts.length - 1}
          onWindow={onWindow}
          onNotice={onNotice}
        />
      ))}
      <div className="hidden items-center gap-2.5 rounded-b-panel bg-foreground/[0.02] px-5 py-3 md:flex">
        <span className="flex-1 text-xs text-muted-foreground">
          {app.note ??
            `When the agent names no account, it uses the primary. Up to ${MAX_ACCOUNTS_PER_APP} accounts.`}
        </span>
        <LevelsPopover app={app} />
        <AskFirstLink app={app.app} />
      </div>
      <AskFirstLink app={app.app} row className="md:hidden" />
    </div>
  );
}

/** "What each level allows": the app's three levels (Paper 44). */
function LevelsPopover({ app }: { app: IntegrationApp }) {
  return (
    <Popover>
      <PopoverTrigger className="shrink-0 text-xs font-medium text-foreground underline underline-offset-2">
        What each level allows
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 gap-0 p-0">
        {app.levels.map((l) => (
          <div
            key={l.level}
            className="flex flex-col gap-0.5 border-b border-border px-4 py-3 last:border-0"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
              {l.name}
              {l.level === "write" ? (
                <span className="text-xs font-medium text-moss">Recommended</span>
              ) : null}
            </span>
            <span className="text-meta text-muted-foreground">{l.allows}</span>
          </div>
        ))}
        <p className="px-4 py-3 text-meta font-medium text-copper">{app.consent}</p>
      </PopoverContent>
    </Popover>
  );
}

/** An account's changes, and the notice each leaves on the page. */
function useAccountActions(
  app: IntegrationApp,
  a: Account,
  label: string,
  onNotice: (title: string) => void,
) {
  const change = useChangeAccount();
  const disconnect = useDisconnectAccount();
  const retry = useRetryDisconnect();
  const reconnect = useConnect();
  return {
    change,
    retry,
    reconnect,
    removing: disconnect.isPending || retry.isPending,
    error: change.error ?? disconnect.error ?? retry.error ?? reconnect.error,
    doReconnect: () => {
      const started = {
        app: app.app,
        name: app.name,
        provider: app.provider.name,
        account_id: a.id,
        label,
      };
      // The sign-in's tab opens now, in the click, so no popup blocker stops it.
      reconnect.mutate({
        app: app.app,
        account_id: a.id,
        started,
        tab: openSignInTab(undefined, started),
      });
    },
    retryDisconnect: () =>
      retry.mutate(a.id, {
        onSuccess: (res) => {
          if (res?.account) return;
          onNotice(
            disconnectNotice({
              label,
              app: app.name,
              next: null,
              revoked: res?.revoked !== false,
              provider: app.provider.name,
            }),
          );
        },
      }),
    makePrimary: () =>
      change.mutate(
        { id: a.id, primary: true },
        { onSuccess: () => onNotice(`${label} is now the primary ${app.name} account.`) },
      ),
    // Share or Just you (slice 6a §5.4): the crew's turns can use it, or only yours.
    share: (shared: boolean) =>
      change.mutate(
        { id: a.id, shared },
        {
          onSuccess: () =>
            onNotice(shared ? `${label} is shared with the crew.` : `${label} is just yours now.`),
        },
      ),
    doDisconnect: () => {
      // The oldest other account becomes primary (spec §5.3), and the notice says so.
      const next = app.accounts
        .filter((o) => o.id !== a.id && o.status === "active")
        .sort((x, y) => x.created_at.localeCompare(y.created_at))[0];
      disconnect.mutate(a.id, {
        onSuccess: (res) => {
          // `202 { account }`: the revoke failed and the row says so.
          if (res?.account) return;
          onNotice(
            disconnectNotice({
              label,
              app: app.name,
              next: a.primary && next ? (next.label ?? next.email) : null,
              revoked: res?.revoked !== false,
              provider: app.provider.name,
            }),
          );
        },
      });
    },
  };
}

/** What the row's action can do: nothing to start while its Disconnect runs (Devin on #251). */
function rowActionOf(a: Account, v: RowViewT, act: ReturnType<typeof useAccountActions>) {
  const open = !act.removing;
  return {
    expired: open && a.status === "expired",
    failedRevoke: open && v.failedRevoke,
    busy: act.reconnect.isPending || act.retry.isPending || act.removing,
  };
}

/** Under an expired account, Reconnect; under a revoke that didn't finish, Try again. */
function RowAction({
  appName,
  provider,
  expired,
  failedRevoke,
  busy,
  onReconnect,
  onRetry,
}: {
  appName: string;
  /** Where a person removes Crew's access themselves when a revoke didn't finish (spec §5.4). */
  provider: Provider;
  expired: boolean;
  failedRevoke: boolean;
  busy: boolean;
  onReconnect: () => void;
  onRetry: () => void;
}) {
  if (expired) {
    return (
      <Button
        onClick={onReconnect}
        disabled={busy}
        className={cn(SOLID_RED, "mt-1 h-10 w-full text-sm md:h-7.5 md:w-fit md:px-3 md:text-meta")}
      >
        <span className="md:hidden">Reconnect {appName}</span>
        <span className="hidden md:inline">Reconnect</span>
      </Button>
    );
  }
  if (!failedRevoke) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-3">
      <Button onClick={onRetry} disabled={busy} className={cn(SOLID_RED, "h-7.5 px-3 text-meta")}>
        Try again
      </Button>
      <a
        href={provider.access}
        target="_blank"
        rel="noreferrer"
        className="text-meta font-medium text-foreground underline underline-offset-2"
      >
        Your {provider.name} account's access
      </a>
    </div>
  );
}

/** Disconnect asks once more (spec §5.1). */
function DisconnectConfirm({
  label,
  provider,
  open,
  onOpenChange,
  onConfirm,
}: {
  label: string;
  provider: Provider;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const words = disconnectWords(provider);
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Disconnect {label}?</AlertDialogTitle>
          <AlertDialogDescription>
            {words.line}
            {words.link ? (
              <>
                {" "}
                <a
                  href={words.link.href}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-foreground underline underline-offset-2"
                >
                  {words.link.text}
                </a>
              </>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <Button
            className={SOLID_RED}
            onClick={() => {
              onOpenChange(false);
              onConfirm();
            }}
          >
            Disconnect
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** What a row shows of its account's level and state. */
function rowView(app: IntegrationApp, a: Account, state: RowState) {
  const level = app.levels.find((l) => l.level === a.level);
  const failedRevoke = a.status === "disconnecting" && state.tone === "red";
  return {
    state,
    level,
    levelName: level?.name ?? a.level,
    failedRevoke,
    manageable: a.status === "active" || a.status === "expired",
    tinted: a.status === "expired" || failedRevoke,
  };
}

type RowViewT = ReturnType<typeof rowView>;

/** A desktop row's fixed columns: the level and what it allows, then the state (Paper 40). */
function RowColumns({ view }: { view: RowViewT }) {
  return (
    <>
      <div className="hidden w-50 shrink-0 flex-col gap-0.5 md:flex">
        <span className="text-sm text-foreground">{view.levelName}</span>
        <span className="text-meta text-muted-foreground">{view.level?.brief}</span>
      </div>
      <StateLabel tone={view.state.tone} className="hidden w-45 shrink-0 md:flex">
        {view.state.text}
      </StateLabel>
    </>
  );
}

/** The ⋯ menu, or its empty slot so the columns line up. */
function RowMenu({ show, ...props }: Parameters<typeof AccountMenu>[0] & { show: boolean }) {
  if (!show) return <span className="hidden size-8 shrink-0 md:block" />;
  return <AccountMenu {...props} />;
}

/** The name, else the email; a connect waiting on Google is a new account. */
const labelOf = (app: IntegrationApp, a: Account) =>
  a.label ?? (a.status === "connecting" ? "New account" : app.name);

function RowName({ label, primary }: { label: string; primary: boolean }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="truncate text-sm font-medium text-foreground">{label}</span>
      {primary ? <PrimaryTag /> : null}
    </div>
  );
}

/** Under the name: the email or date, the reason, and on a phone the level and state (Paper 40b). */
function RowLines({ state, levelName }: { state: RowState; levelName: string }) {
  return (
    <>
      {state.under ? (
        <span className="truncate text-meta text-muted-foreground tabular-nums">{state.under}</span>
      ) : null}
      {state.wait ? (
        <span className="text-meta text-muted-foreground tabular-nums" data-wait>
          {state.wait}
        </span>
      ) : null}
      {state.reason ? <p className="text-meta leading-[18px] text-copy">{state.reason}</p> : null}
      <div className="flex flex-wrap items-center gap-1.5 pt-1 md:hidden">
        <span className="text-meta text-copy">{levelName}</span>
        <span className="text-meta text-hint">·</span>
        <StateLabel tone={state.tone}>{state.text}</StateLabel>
      </div>
    </>
  );
}

function AccountRow({
  app,
  account: a,
  timeZone,
  now,
  last,
  onWindow,
  onNotice,
}: {
  app: IntegrationApp;
  account: Account;
  timeZone: string;
  /** The server's time, for a sign-in's countdown. */
  now: number;
  last: boolean;
  onWindow: (app: WindowApp, mode: WindowMode) => void;
  onNotice: (title: string) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const label = labelOf(app, a);
  const act = useAccountActions(app, a, label, onNotice);
  const v = rowView(
    app,
    a,
    rowState(a, timeZone, { disconnecting: act.removing, now, provider: app.provider.name }),
  );
  const { state, levelName } = v;

  return (
    <div
      data-account={a.id}
      data-status={a.status}
      data-level={a.level}
      className={cn(
        "flex items-start gap-3 py-3 pr-2 pl-4 md:gap-5 md:py-3.5 md:pr-5 md:pl-16.5",
        !state.reason && "md:items-center",
        v.tinted && "bg-destructive/[0.03]",
        !last && "border-b border-border",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {renaming ? (
          <RenameForm
            account={a}
            busy={act.change.isPending}
            onCancel={() => setRenaming(false)}
            onSave={(name) =>
              act.change.mutate({ id: a.id, name }, { onSuccess: () => setRenaming(false) })
            }
          />
        ) : (
          <RowName label={label} primary={a.primary} />
        )}
        <RowLines state={state} levelName={levelName} />
        <RowAction
          appName={app.name}
          provider={app.provider}
          {...rowActionOf(a, v, act)}
          onReconnect={act.doReconnect}
          onRetry={act.retryDisconnect}
        />
        {v.manageable ? (
          <ShareSelect
            at="phone"
            shared={a.shared}
            label={label}
            reach={`up to ${levelName}`}
            busy={act.change.isPending || act.removing}
            onChange={act.share}
          />
        ) : null}
        <ErrorLine error={act.error} />
      </div>
      <RowColumns view={v} />
      {v.manageable ? (
        <ShareSelect
          at="desktop"
          shared={a.shared}
          label={label}
          reach={`up to ${levelName}`}
          busy={act.change.isPending || act.removing}
          onChange={act.share}
        />
      ) : (
        <span className="hidden w-35 shrink-0 md:block" />
      )}
      <RowMenu
        account={a}
        show={v.manageable}
        disabled={act.removing}
        levelName={levelName}
        onAccess={() => onWindow(app, { kind: "access", account: a })}
        onReconnect={act.doReconnect}
        onRename={() => setRenaming(true)}
        onPrimary={act.makePrimary}
        onDisconnect={() => setConfirm(true)}
      />
      <DisconnectConfirm
        label={label}
        provider={app.provider}
        open={confirm}
        onOpenChange={setConfirm}
        onConfirm={act.doDisconnect}
      />
    </div>
  );
}

/** The ⋯ menu (Paper 44): an expired account's leads with Reconnect and has no Make primary. */
function AccountMenu({
  account: a,
  disabled,
  levelName,
  onAccess,
  onReconnect,
  onRename,
  onPrimary,
  onDisconnect,
}: {
  account: Account;
  /** While its Disconnect runs (PR 6). */
  disabled?: boolean;
  levelName: string;
  onAccess: () => void;
  onReconnect: () => void;
  onRename: () => void;
  onPrimary: () => void;
  onDisconnect: () => void;
}) {
  const access = (
    <DropdownMenuItem onClick={onAccess} className="flex-col items-start gap-0.5">
      <span className="font-medium">Change access</span>
      <span className="text-xs text-muted-foreground">Now {levelName}.</span>
    </DropdownMenuItem>
  );
  const reconnect = (
    <DropdownMenuItem onClick={onReconnect} className="font-medium">
      Reconnect
    </DropdownMenuItem>
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={disabled}
        render={
          <Button
            variant="ghost"
            size="icon"
            className="size-9 shrink-0 text-muted-foreground md:size-8"
            aria-label={`${a.label ?? "Account"}: more`}
          />
        }
      >
        <Ellipsis className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-65 p-1.5">
        {a.status === "expired" ? (
          <>
            {reconnect}
            {access}
          </>
        ) : (
          <>
            {access}
            {reconnect}
          </>
        )}
        <DropdownMenuItem onClick={onRename} className="font-medium">
          Rename
        </DropdownMenuItem>
        {a.status === "active" ? (
          <DropdownMenuItem
            onClick={onPrimary}
            disabled={a.primary}
            className="justify-between font-medium"
          >
            Make primary
            {a.primary ? <span className="text-xs font-normal text-hint">It is</span> : null}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onDisconnect} variant="destructive" className="font-medium">
          Disconnect
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Rename, in the row (Paper 44): 1–40 characters; empty goes back to the email. */
function RenameForm({
  account,
  busy,
  onSave,
  onCancel,
}: {
  account: Account;
  busy: boolean;
  onSave: (name: string | null) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(account.name ?? "");
  const trimmed = name.trim();
  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed.length <= 40) onSave(trimmed || null);
      }}
    >
      <Input
        autoFocus
        aria-label="Name"
        value={name}
        maxLength={40}
        placeholder={account.email ?? ""}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Escape" && onCancel()}
        className="h-8.5 min-w-0 flex-1"
      />
      <Button type="submit" disabled={busy} className="h-8.5">
        Save
      </Button>
      <Button type="button" variant="ghost" className="h-8.5" onClick={onCancel}>
        Cancel
      </Button>
    </form>
  );
}
